/**
 * Reschedule unschedulable matches according to player availability.
 * Also reschedules matches that are currently in a slot where a player is unavailable
 * (wrong time, exclusion date, or holiday). Tries swap when possible.
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDate } from '../utils/dateFormat';
import { generateSlots, assignSlotsNoBackToBack, migrateScheduleConfig } from '../utils/scheduleStrategy';
import { getAvailabilityForPlayers, createAvailabilityChecker } from './playerAvailabilityService';
import { getMatchesWithUnavailablePlayers } from './availabilityAuditService';
import { ensureVenueBlockForMatch } from './venueBlockService';
import { toUtcMs } from '../utils/dateTimeUtils';

const LOG_TYPES = { RESCHEDULE_COMPLETED: 'reschedule_completed' };

function toPlainDate(dateOrTimestamp) {
  const ms = toUtcMs(dateOrTimestamp);
  return ms != null ? new Date(ms) : null;
}

function getSlotKey(dateOrTimestamp, timeStr) {
  const d = toPlainDate(dateOrTimestamp);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}|${(timeStr || '').trim()}`;
}

/** Get participant IDs for a match (for availability check). */
function getParticipantIds(m) {
  if (m.matchType === 'doubles') {
    return [...(m.team1Players || []), ...(m.team2Players || [])];
  }
  return [m.player1Id, m.player2Id].filter(Boolean);
}

/** Convert Firestore match doc to raw format for assignSlotsNoBackToBack (preserves id for lookup). */
function matchDocToRaw(m) {
  const base = { id: m.id, matchType: m.matchType || 'singles' };
  if (m.matchType === 'doubles') {
    return {
      ...base,
      left: {
        teamId: m.team1Id,
        teamName: m.team1Name,
        players: m.team1Players || [],
      },
      right: {
        teamId: m.team2Id,
        teamName: m.team2Name,
        players: m.team2Players || [],
      },
    };
  }
  return {
    ...base,
    left: { id: m.player1Id },
    right: { id: m.player2Id },
  };
}

/**
 * Reschedule unschedulable matches by player availability.
 * Only processes tournaments with askPlayerAvailability enabled.
 * @param {{ gameId?: string }} [options]
 * @returns {{ scheduled: number, stillUnschedulable: number, errors: string[], skippedTournaments?: string[] }}
 */
export async function rescheduleUnschedulableByAvailability(options = {}) {
  const gameIdFilter = options.gameId || null;
  const errors = [];
  let scheduled = 0;
  let stillUnschedulable = 0;
  const skippedTournaments = [];

  const unschedulableSnap = await getDocs(
    query(collection(db, 'matches'), where('status', '==', 'unschedulable'))
  );
  let unschedulableMatches = unschedulableSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (gameIdFilter) {
    unschedulableMatches = unschedulableMatches.filter((m) => (m.gameId || 'table-tennis') === gameIdFilter);
  }

  const byTournament = new Map();
  for (const m of unschedulableMatches) {
    const tid = m.tournamentId || '_none';
    if (!byTournament.has(tid)) byTournament.set(tid, []);
    byTournament.get(tid).push(m);
  }

  // Include tournaments that have scheduled matches in bad slots (players unavailable) but no unschedulable
  const tournamentsSnap = await getDocs(
    query(collection(db, 'tournaments'), where('askPlayerAvailability', '==', true))
  );
  for (const tDoc of tournamentsSnap.docs) {
    const tid = tDoc.id;
    if (byTournament.has(tid)) continue;
    try {
      const badSlotResults = await getMatchesWithUnavailablePlayers(tid);
      if (badSlotResults.length > 0) byTournament.set(tid, []);
    } catch {
      // ignore
    }
  }

  for (const [tournamentId, unschedulableOnly] of byTournament) {
    if (tournamentId === '_none') continue;

    let tournament = null;
    try {
      const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tSnap.exists()) continue;
      tournament = { id: tSnap.id, ...tSnap.data() };
    } catch {
      continue;
    }

    if (!tournament.askPlayerAvailability) {
      skippedTournaments.push(tournament.name || tournamentId);
      continue;
    }

    const config = migrateScheduleConfig(tournament.scheduleConfig || {});
    const hasSlots = !!(config.scheduleStart != null && config.scheduleEnd != null && Array.isArray(config.weekdays) && config.weekdays.length > 0);
    if (!hasSlots) {
      skippedTournaments.push(`${tournament.name || tournamentId} (no schedule config)`);
      continue;
    }

    // Include matches that are scheduled but in a slot where a player is unavailable (wrong day/time or exclusion/holiday)
    let badSlotResults = [];
    try {
      badSlotResults = await getMatchesWithUnavailablePlayers(tournamentId);
    } catch (e) {
      console.warn('[availabilityReschedule] audit failed for', tournamentId, e);
    }
    const badSlotMatches = badSlotResults.map((r) => r.match);
    const matchesToAssign = [...unschedulableOnly];
    for (const m of badSlotMatches) {
      if (!matchesToAssign.some((x) => x.id === m.id)) matchesToAssign.push(m);
    }
    if (matchesToAssign.length === 0) continue;

    const allPlayerIds = new Set();
    for (const m of matchesToAssign) {
      getParticipantIds(m).forEach((id) => allPlayerIds.add(id));
    }
    const availabilityByPlayer = await getAvailabilityForPlayers(tournamentId, [...allPlayerIds]);
    const availabilityChecker = createAvailabilityChecker(availabilityByPlayer);

    const scheduledSnap = await getDocs(
      query(
        collection(db, 'matches'),
        where('tournamentId', '==', tournamentId),
        where('status', 'in', ['scheduled', 'in-progress'])
      )
    );
    const allScheduled = scheduledSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const rescheduleIds = new Set(matchesToAssign.map((m) => m.id));
    const takenSlots = new Set();
    for (const m of allScheduled) {
      if (rescheduleIds.has(m.id)) continue;
      if (m.scheduledDate) {
        const key = getSlotKey(m.scheduledDate, m.scheduledTime);
        if (key) takenSlots.add(key);
      }
    }

    const minSlots = Math.max(matchesToAssign.length + 20, 100);
    const allSlots = generateSlots(config, minSlots);
    const availableSlots = allSlots.filter((s) => {
      const key = getSlotKey(s.date, s.time);
      return !takenSlots.has(key);
    });

    const matchById = new Map(matchesToAssign.map((m) => [m.id, m]));
    const withMeta = matchesToAssign.map((m) => ({ raw: matchDocToRaw(m) }));
    let assigned = assignSlotsNoBackToBack(withMeta, availableSlots, null, availabilityChecker);

    // Try swap: for still-unschedulable matches, see if swapping with an assigned match helps
    const assignedWithSlot = assigned.filter((a) => !a.unschedulable && a.slot);
    let stillUnschedulableList = assigned.filter((a) => a.unschedulable);
    for (const u of stillUnschedulableList) {
      const uIds = getParticipantIds(u.match);
      for (let i = 0; i < assignedWithSlot.length; i++) {
        const a = assignedWithSlot[i];
        const slot = a.slot;
        if (!availabilityChecker(slot, uIds)) continue;
        const aIds = getParticipantIds(a.match);
        const otherUsedSlots = new Set(
          assignedWithSlot.filter((x) => x !== a).map((x) => getSlotKey(x.slot.date, x.slot.time))
        );
        const openForA = availableSlots.filter((s) => {
          const k = getSlotKey(s.date, s.time);
          return !takenSlots.has(k) && !otherUsedSlots.has(k) && availabilityChecker(s, aIds);
        });
        if (openForA.length === 0) continue;
        const newSlotForA = openForA[0];
        u.slot = slot;
        u.unschedulable = false;
        a.slot = newSlotForA;
        assignedWithSlot[i] = a;
        stillUnschedulableList = stillUnschedulableList.filter((x) => x !== u);
        break;
      }
    }

    const durationMins = config.matchDuration || 30;
    const venueId = tournament.defaultVenueId || null;

    for (const { match: m, slot, unschedulable } of assigned) {
      const originalMatch = matchById.get(m.id);
      if (!originalMatch) continue;
      if (unschedulable || !slot) {
        stillUnschedulable++;
        continue;
      }

      try {
        const slotDate = slot.date instanceof Date ? new Date(slot.date) : new Date(slot.date);
        const updatedMatch = {
          ...originalMatch,
          scheduledDate: slotDate,
          scheduledTime: slot.time,
          venueId: originalMatch.venueId || venueId,
        };
        const venueBlockId = updatedMatch.venueId
          ? await ensureVenueBlockForMatch(updatedMatch, durationMins)
          : null;

        const updatePayload = {
          scheduledDate: slotDate,
          scheduledTime: slot.time,
          status: 'scheduled',
          updatedAt: Timestamp.now(),
        };
        if (venueBlockId != null) updatePayload.venueBlockId = venueBlockId;
        if (updatedMatch.venueId) updatePayload.venueId = updatedMatch.venueId;

        await updateDoc(doc(db, 'matches', originalMatch.id), updatePayload);

        const oldDateObj = originalMatch.scheduledDate ? toPlainDate(originalMatch.scheduledDate) : null;
        await addDoc(collection(db, 'matchUpdateLogs'), {
          matchId: originalMatch.id,
          type: LOG_TYPES.RESCHEDULE_COMPLETED,
          oldDate: oldDateObj ? formatDate(oldDateObj) : '',
          oldTime: (originalMatch.scheduledTime || '').toString(),
          newDate: formatDate(slotDate),
          newTime: slot.time,
          performedBy: null,
          performedByName: 'Admin reschedule (by availability)',
          performedAt: Timestamp.now(),
        });

        scheduled++;
        takenSlots.add(getSlotKey(slotDate, slot.time));
      } catch (err) {
        console.error('[availabilityReschedule] match', originalMatch.id, err);
        errors.push(`Match ${originalMatch.id}: ${err.message}`);
      }
    }
  }

  return { scheduled, stillUnschedulable, errors, skippedTournaments };
}
